import { withCappedEntry } from '@lib/insurance-packages/data-access';
import { createReducer, on } from '@ngrx/store';

import { ContractStatus, MemberContract } from '@lib/core/models/contract.interface';

import {
  fetchMemberActiveCoverage,
  fetchMemberActiveCoverageFailure,
  fetchMemberActiveCoverageSuccess,
  fetchMemberAppointmentsSuccess,
  fetchMemberCoverageOptionDiagnoses,
  fetchMemberCoverageOptionDiagnosesFailure,
  fetchMemberCoverageOptionDiagnosesSuccess,
  fetchMemberPackage,
  fetchMemberPackageFailure,
  fetchMemberPackageSuccess,
  fetchMemberProfileSuccess,
  resetMemberHubState,
  setMemberActiveCoverageSearch,
  toggleMemberActiveCoverageCategories,
  toggleMemberActiveCoverageOption,
} from './member-hub.actions';
import { MemberActiveCoverageState, MemberHubState } from './member-hub.state.interface';

const OPTION_DIAGNOSES_CACHE_LIMIT = 10;

const initialActiveCoverageState: MemberActiveCoverageState = {
  coverage: null,
  search: '',
  collapsedOptions: {},
  expandedCategories: {},
  optionDiagnoses: {},
};

const initialState: MemberHubState = {
  memberProfile: null,
  memberPackage: null,
  memberAppointments: null,
  activeCoverage: initialActiveCoverageState,
};

export const memberHubReducer = createReducer(
  initialState,
  on(fetchMemberProfileSuccess, (state, { memberProfile }) => {
    const memberContracts = [...memberProfile.contracts].sort((contract: MemberContract) => (contract.status === ContractStatus.Active ? -1 : 1));
    const updatedMemberProfile = { ...memberProfile, contracts: memberContracts };
    return {
      ...state,
      memberProfile: updatedMemberProfile,
    };
  }),
  on(fetchMemberPackage, (state, { userId }) => ({
    ...state,
    memberPackage: { userId, data: null, loading: true, error: false },
  })),
  on(fetchMemberPackageSuccess, (state, { userId, memberPackage }) => ({
    ...state,
    memberPackage: { userId, data: memberPackage, loading: false, error: false },
  })),
  on(fetchMemberPackageFailure, (state, { userId }) => ({
    ...state,
    memberPackage: { userId, data: null, loading: false, error: true },
  })),
  on(fetchMemberAppointmentsSuccess, (state, { appointments }) => ({
    ...state,
    memberAppointments: appointments,
  })),
  on(fetchMemberActiveCoverage, (state, { userId }) => ({
    ...state,
    activeCoverage: { ...state.activeCoverage, coverage: { userId, data: null, loading: true, error: false } },
  })),
  on(fetchMemberActiveCoverageSuccess, (state, { userId, coverage }) => ({
    ...state,
    activeCoverage: { ...state.activeCoverage, coverage: { userId, data: coverage, loading: false, error: false } },
  })),
  on(fetchMemberActiveCoverageFailure, (state, { userId }) => ({
    ...state,
    activeCoverage: { ...state.activeCoverage, coverage: { userId, data: null, loading: false, error: true } },
  })),
  on(fetchMemberCoverageOptionDiagnoses, (state, { optionId }) => ({
    ...state,
    activeCoverage: {
      ...state.activeCoverage,
      optionDiagnoses: withCappedEntry(state.activeCoverage.optionDiagnoses, optionId, { data: [], loading: true, error: false }, OPTION_DIAGNOSES_CACHE_LIMIT),
    },
  })),
  on(fetchMemberCoverageOptionDiagnosesSuccess, (state, { optionId, diagnoses }) => ({
    ...state,
    activeCoverage: {
      ...state.activeCoverage,
      optionDiagnoses: withCappedEntry(state.activeCoverage.optionDiagnoses, optionId, { data: diagnoses, loading: false, error: false }, OPTION_DIAGNOSES_CACHE_LIMIT),
    },
  })),
  on(fetchMemberCoverageOptionDiagnosesFailure, (state, { optionId }) => ({
    ...state,
    activeCoverage: {
      ...state.activeCoverage,
      optionDiagnoses: withCappedEntry(state.activeCoverage.optionDiagnoses, optionId, { data: [], loading: false, error: true }, OPTION_DIAGNOSES_CACHE_LIMIT),
    },
  })),
  on(setMemberActiveCoverageSearch, (state, { search }) => ({
    ...state,
    activeCoverage: { ...state.activeCoverage, search },
  })),
  on(toggleMemberActiveCoverageOption, (state, { optionId }) => ({
    ...state,
    activeCoverage: { ...state.activeCoverage, collapsedOptions: { ...state.activeCoverage.collapsedOptions, [optionId]: !state.activeCoverage.collapsedOptions[optionId] } },
  })),
  on(toggleMemberActiveCoverageCategories, (state, { itemId }) => ({
    ...state,
    activeCoverage: { ...state.activeCoverage, expandedCategories: { ...state.activeCoverage.expandedCategories, [itemId]: !state.activeCoverage.expandedCategories[itemId] } },
  })),
  on(resetMemberHubState, state => ({
    ...initialState,
    activeCoverage: {
      ...initialActiveCoverageState,
      optionDiagnoses: state.activeCoverage.optionDiagnoses,
    },
  }))
);
